import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { approvalLinkService, getErrorMessage } from '../../services/apiService';
import { fileKind, fmtSize } from './documents';

/**
 * One supplier document, shown inside the decision page (PDF or image), with previous / next
 * through the list. The file is fetched as a blob and shown from a blob: URL, which no framing
 * rule can block; anything that cannot be shown is offered as a download.
 */
const DocumentViewer = ({ token, docs, index, onIndex, onClose, onDownload }) => {
  const doc = docs[index];
  const kind = fileKind(doc);
  const [view, setView] = useState({ loading: true, url: null, error: null });
  const closeRef = useRef(null);
  // Chrome on Android and some older browsers cannot show a PDF inside a page.
  const inlinePdf = typeof navigator === 'undefined' || navigator.pdfViewerEnabled !== false;

  useEffect(() => {
    let active = true;
    let url = null;
    if (kind === 'other') {
      setView({ loading: false, url: null, error: null });
      return undefined;
    }
    setView({ loading: true, url: null, error: null });
    approvalLinkService
      .fetchDocument(token, doc.id)
      .then((blob) => {
        if (!active) return;
        const typed = blob.type ? blob : new Blob([blob], { type: kind === 'pdf' ? 'application/pdf' : 'image/png' });
        url = URL.createObjectURL(typed);
        setView({ loading: false, url, error: null });
      })
      .catch((e) => active && setView({ loading: false, url: null, error: getErrorMessage(e) || 'Could not open this document.' }));
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [token, doc.id, kind]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      else if (e.key === 'ArrowRight' && index < docs.length - 1) onIndex(index + 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, docs.length, onClose, onIndex]);

  const label = doc.type_label || doc.file_name;
  const meta = [doc.file_name, fmtSize(doc.file_size)].filter(Boolean).join(' · ');
  const iconBtn = 'p-2 rounded-lg text-slate-200 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60';

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-950/95" role="dialog" aria-modal="true" aria-label={`Document: ${label}`} data-testid="document-viewer">
      <header className="flex items-center gap-2 px-3 sm:px-5 py-2.5 bg-slate-900 text-white border-b border-white/10">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate" data-testid="document-viewer-title">{label}</p>
          <p className="text-xs text-slate-400 truncate">
            {meta}
            <span className="text-slate-500"> · {index + 1} of {docs.length}</span>
          </p>
        </div>
        <button type="button" className={iconBtn} onClick={() => onIndex(index - 1)} disabled={index === 0} aria-label="Previous document" title="Previous (←)">
          <Icon name="ChevronLeft" size={20} />
        </button>
        <button type="button" className={iconBtn} onClick={() => onIndex(index + 1)} disabled={index === docs.length - 1} aria-label="Next document" title="Next (→)">
          <Icon name="ChevronRight" size={20} />
        </button>
        <span className="w-px h-6 bg-white/15 mx-1" aria-hidden="true" />
        {view.url && (
          <a className={iconBtn} href={view.url} target="_blank" rel="noopener noreferrer" aria-label="Open in a new tab" title="Open in a new tab">
            <Icon name="ExternalLink" size={18} />
          </a>
        )}
        <button type="button" className={iconBtn} onClick={() => onDownload(doc)} aria-label="Download" title="Download">
          <Icon name="Download" size={18} />
        </button>
        <button ref={closeRef} type="button" className={iconBtn} onClick={onClose} aria-label="Close document" title="Close (Esc)">
          <Icon name="X" size={20} />
        </button>
      </header>

      <div className="flex-1 min-h-0 flex items-center justify-center p-2 sm:p-5" onClick={(e) => e.target === e.currentTarget && onClose()}>
        {view.loading ? (
          <div className="text-center text-slate-300">
            <div className="animate-spin rounded-full h-9 w-9 border-2 border-slate-500 border-t-white mx-auto" />
            <p className="mt-3 text-sm">Opening {label}…</p>
          </div>
        ) : view.error ? (
          <div className="max-w-sm rounded-xl bg-white p-6 text-center">
            <Icon name="FileWarning" size={32} className="mx-auto text-red-500" />
            <p className="mt-3 text-sm text-slate-700">{view.error}</p>
          </div>
        ) : kind === 'image' ? (
          <img src={view.url} alt={label} className="max-h-full max-w-full object-contain rounded-lg bg-white shadow-2xl" />
        ) : kind === 'pdf' && inlinePdf ? (
          <iframe src={`${view.url}#navpanes=0&view=FitH`} title={label} className="w-full h-full max-w-5xl rounded-lg bg-white shadow-2xl" />
        ) : (
          <div className="max-w-sm rounded-xl bg-white p-6 text-center">
            <Icon name="FileText" size={32} className="mx-auto text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-800">{label}</p>
            <p className="mt-1 text-sm text-slate-600">
              {kind === 'pdf' ? 'This browser cannot show a PDF inside the page.' : 'This kind of file cannot be shown here.'}
            </p>
            <div className="mt-4 flex flex-col sm:flex-row justify-center gap-2">
              {view.url && (
                <Button variant="outline" onClick={() => window.open(view.url, '_blank', 'noopener,noreferrer')} iconName="ExternalLink" iconPosition="left">
                  Open
                </Button>
              )}
              <Button onClick={() => onDownload(doc)} iconName="Download" iconPosition="left">Download</Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default DocumentViewer;
